import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  LiveMatch, MatchRequest, Streamer,
  getMatches, createMatch, updateMatch, deleteMatch,
  uploadMatchPoster,
  fetchTikTokProfile, getMatchPosterUrl, getStreamers,
  getMatchRequests, updateMatchRequest, acceptMatchRequest, deleteMatchRequest,
  getSmsReminder, setSmsReminder, SmsReminder,
} from './api';
import PosterEditor from './PosterEditor';
import { useAuth } from './AuthContext';

// ── Helpers ──────────────────────────────────────────────────

function fmtDate(s: string) {
  try {
    const d = new Date(s.replace(' ', 'T'));
    return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return s; }
}

function fmtTime(s: string) {
  try {
    const d = new Date(s.replace(' ', 'T'));
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function fmtShort(s: string) {
  try {
    const d = new Date(s.replace(' ', 'T'));
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) + ' · ' + d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  } catch { return s; }
}

const TYPE_LABEL: Record<string, string> = {
  officiel: '🏆 Officiel',
  anniversaire: '🎂 Anniversaire',
  autre: '✏️ Personnalisé',
};
const TYPE_COLOR: Record<string, string> = {
  officiel: '#7c3aed',
  anniversaire: '#ea580c',
  autre: '#0369a1',
};

// ── Mini avatar ──────────────────────────────────────────────

function MiniAvatar({ url, name, size = 32 }: { url?: string | null; name?: string | null; size?: number }) {
  const [err, setErr] = useState(false);
  const initial = (name || '?').charAt(0).toUpperCase();
  const src = url && /tiktokcdn|ibyteimg|muscdn|bytecdn/i.test(url)
    ? `/api/gift-image?url=${encodeURIComponent(url)}` : url;
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: '#e2e8f0', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.38), fontWeight: 700, color: '#64748b' }}>
      {src && !err
        ? <img src={src} alt={name || ''} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={() => setErr(true)} />
        : initial}
    </div>
  );
}

// ── Match list card ──────────────────────────────────────────

function MatchCard({ match, selected, onClick }: { match: LiveMatch; selected: boolean; onClick: () => void }) {
  const accent = TYPE_COLOR[match.match_type] || '#2563eb';
  return (
    <div onClick={onClick} style={{
      background: '#fff', borderRadius: 10, overflow: 'hidden', cursor: 'pointer', marginBottom: 8,
      border: selected ? `2px solid ${accent}` : '1px solid #e2e8f0',
      boxShadow: selected ? `0 0 0 3px ${accent}22` : '0 1px 2px rgba(0,0,0,0.04)',
      display: 'flex', flexDirection: 'column', flexShrink: 0, transition: 'all 0.12s',
    }}>
      {match.poster_filename ? (
        <img src={getMatchPosterUrl(match.poster_filename)} alt="" loading="lazy"
          style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block', background: '#f1f5f9' }} />
      ) : (
        <div style={{ width: '100%', height: 90, background: `${accent}1f`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 38 }}>🥊</div>
      )}
      <div style={{ padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <MiniAvatar url={match.streamer1_avatar} name={match.streamer1_name} size={24} />
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {match.streamer1_name || '?'}
          </span>
          <span style={{ fontSize: 10, fontWeight: 900, color: '#94a3b8', flexShrink: 0 }}>VS</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>
            {match.streamer2_name || '?'}
          </span>
          <MiniAvatar url={match.streamer2_avatar_url} name={match.streamer2_name} size={24} />
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtShort(match.scheduled_at)}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: accent + '18', color: accent }}>
            {TYPE_LABEL[match.match_type] || match.match_label || match.match_type}
          </span>
          <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: match.status === 'completed' ? '#dcfce7' : '#fef3c7', color: match.status === 'completed' ? '#16a34a' : '#92400e', fontWeight: 600 }}>
            {match.status === 'completed' ? '✅ Terminé' : '🗓 Programmé'}
          </span>
          {match.forfeit && match.forfeit !== 'none' && (
            <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 20, background: '#fef2f2', color: '#dc2626', fontWeight: 700 }}>⚠ Forfait</span>
          )}
          {match.status === 'completed' && match.score1 != null && match.score2 != null && (
            <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginLeft: 'auto' }}>{match.score1} — {match.score2}</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Carte de demande (admin) ─────────────────────────────────

function RequestAdminCard({ req, streamers, actionState, onAccept, onReject, onDelete }: {
  req: MatchRequest;
  streamers: Streamer[];
  actionState?: string;
  onAccept: () => void;
  onReject: (notes?: string) => void;
  onDelete: () => void;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const typeColor = TYPE_COLOR[req.match_type] || '#64748b';

  return (
    <div style={{ background: '#fff', border: '1px solid #fde68a', borderLeft: `4px solid ${typeColor}`, borderRadius: 10, padding: '12px 14px', marginBottom: 8 }}>
      {/* En-tête */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <MiniAvatar url={req.requester_avatar} name={req.requester_name} size={30} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {req.requester_name || '?'}
          </div>
          <div style={{ fontSize: 10, color: '#64748b' }}>demande un match</div>
        </div>
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: typeColor + '18', color: typeColor, flexShrink: 0 }}>
          {TYPE_LABEL[req.match_type] || req.match_type}
          {req.match_type === 'autre' && req.match_label ? ` · ${req.match_label}` : ''}
        </span>
      </div>

      {/* Date */}
      <div style={{ fontSize: 11, color: '#374151', marginBottom: 6 }}>
        📅 {(() => { try { const d = new Date(req.scheduled_at.replace(' ','T')); return d.toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long'}) + ' · ' + d.toLocaleTimeString('fr-FR',{hour:'2-digit',minute:'2-digit'}); } catch { return req.scheduled_at; } })()}
      </div>

      {/* Adversaire souhaité */}
      <div style={{ fontSize: 11, marginBottom: 10 }}>
        {req.opponent_id ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: '#f0fdf4', color: '#15803d', borderRadius: 20, padding: '2px 8px', fontWeight: 600 }}>
            <MiniAvatar url={req.opponent_avatar} name={req.opponent_name} size={16} />
            vs {req.opponent_name}
          </span>
        ) : (
          <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Adversaire à trouver</span>
        )}
      </div>

      {/* Actions */}
      {!rejectOpen ? (
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={onAccept} disabled={!!actionState}
            style={{ flex: 1, padding: '6px 0', borderRadius: 7, border: 'none', background: '#16a34a', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: actionState ? 0.6 : 1 }}>
            {actionState === 'accepting' ? '…' : '✅ Accepter → Créer le match'}
          </button>
          <button onClick={() => setRejectOpen(true)} disabled={!!actionState}
            style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 11, fontWeight: 600, cursor: 'pointer', opacity: actionState ? 0.6 : 1 }}>
            ✕ Refuser
          </button>
          <button onClick={onDelete} disabled={!!actionState}
            style={{ padding: '6px 8px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#94a3b8', fontSize: 11, cursor: 'pointer', opacity: actionState ? 0.6 : 1 }}
            title="Supprimer">
            🗑
          </button>
        </div>
      ) : (
        <div>
          <input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Raison du refus (optionnel)…"
            style={{ width: '100%', padding: '6px 10px', borderRadius: 7, border: '1px solid #fecaca', fontSize: 12, boxSizing: 'border-box', outline: 'none', marginBottom: 6 }} />
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => { onReject(notes); setRejectOpen(false); }} disabled={actionState === 'rejecting'}
              style={{ flex: 1, padding: '6px 0', borderRadius: 7, border: 'none', background: '#dc2626', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
              {actionState === 'rejecting' ? '…' : 'Confirmer le refus'}
            </button>
            <button onClick={() => setRejectOpen(false)}
              style={{ padding: '6px 10px', borderRadius: 7, border: '1px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 11, cursor: 'pointer' }}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────

export default function LivesMatchs({ isMobile }: { isMobile: boolean }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [matches, setMatches] = useState<LiveMatch[]>([]);
  const [requests, setRequests] = useState<MatchRequest[]>([]);
  const [streamers, setStreamers] = useState<Streamer[]>([]);
  const [selected, setSelected] = useState<LiveMatch | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showRequests, setShowRequests] = useState(true);
  const [filter, setFilter] = useState<'all' | 'scheduled' | 'completed'>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [requestAction, setRequestAction] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [showEditor, setShowEditor] = useState(false);

  // Create form state
  const [form, setForm] = useState({
    match_type: 'officiel',
    match_label: '',
    scheduled_date: '',
    scheduled_time: '21:00',
    streamer1_id: '',
    challenger_mode: 'interne' as 'interne' | 'externe',
    streamer2_id: '',
  });
  const [externalHandle, setExternalHandle] = useState('');
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [externalProfile, setExternalProfile] = useState<{ display_name: string; avatar_url: string | null } | null>(null);
  // Rappel SMS du concurrent 1 (réglage par streamer) — voir CODEX §30
  const [matchReminder, setMatchReminder] = useState<SmsReminder | null>(null);
  const [matchReminderLoading, setMatchReminderLoading] = useState(false);
  const [savingMatchReminder, setSavingMatchReminder] = useState(false);

  // Score edit
  const [editScore, setEditScore] = useState({ score1: '', score2: '', forfeit: '' });

  // Édition des concurrents
  const [editComp, setEditComp] = useState(false);
  const [ec1, setEc1] = useState('');
  const [ec2mode, setEc2mode] = useState<'interne' | 'externe'>('interne');
  const [ec2id, setEc2id] = useState('');
  const [ec2h, setEc2h] = useState('');
  const [ec2prof, setEc2prof] = useState<{ display_name: string; avatar_url: string | null } | null>(null);
  const [ecBusy, setEcBusy] = useState(false);

  const posterInputRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mr, sr, rr] = await Promise.all([getMatches(), getStreamers(), getMatchRequests()]);
      setMatches(mr.data);
      setStreamers(sr.data);
      setRequests(rr.data.filter((r: MatchRequest) => r.status === 'pending'));
    } catch {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Charge le réglage de rappel SMS du concurrent 1 sélectionné
  useEffect(() => {
    if (!form.streamer1_id) { setMatchReminder(null); return; }
    setMatchReminderLoading(true);
    getSmsReminder(Number(form.streamer1_id))
      .then(r => setMatchReminder(r.data))
      .catch(() => setMatchReminder(null))
      .finally(() => setMatchReminderLoading(false));
  }, [form.streamer1_id]);

  const saveMatchReminder = async (patch: { enabled?: boolean; lead_min?: number }) => {
    if (!form.streamer1_id) return;
    setSavingMatchReminder(true);
    try {
      const r = await setSmsReminder(Number(form.streamer1_id), patch);
      setMatchReminder(r.data);
    } catch {}
    setSavingMatchReminder(false);
  };

  useEffect(() => {
    if (selected) {
      setEditScore({
        score1: selected.score1 != null ? String(selected.score1) : '',
        score2: selected.score2 != null ? String(selected.score2) : '',
        forfeit: selected.forfeit && selected.forfeit !== 'none' ? selected.forfeit : '',
      });
      setDeleteConfirm(false);
      setError(null);
      setEditComp(false);
      setEc1(selected.streamer1_id ? String(selected.streamer1_id) : '');
      setEc2mode(selected.streamer2_id ? 'interne' : 'externe');
      setEc2id(selected.streamer2_id ? String(selected.streamer2_id) : '');
      setEc2h(selected.streamer2_id ? '' : (selected.streamer2_name || ''));
      setEc2prof(null);
    }
  }, [selected?.id]);

  const fetchEc2 = async () => {
    if (!ec2h.trim()) return;
    try {
      const r = await fetchTikTokProfile(ec2h.trim().replace(/^@/, ''));
      setEc2prof({ display_name: r.data.display_name, avatar_url: r.data.avatar_url });
    } catch { setEc2prof(null); }
  };

  const saveCompetitors = async () => {
    if (!selected) return;
    if (!ec1) { setError('Sélectionne le Challenger 1'); return; }
    const payload: any = { streamer1_id: parseInt(ec1, 10) };
    if (ec2mode === 'interne') {
      if (!ec2id) { setError('Sélectionne le Challenger 2'); return; }
      const s = streamers.find(x => x.id === parseInt(ec2id, 10));
      payload.streamer2_id = parseInt(ec2id, 10);
      payload.streamer2_name = s?.display_name || s?.username || '';
      payload.streamer2_username = s?.username || null;
    } else {
      const h = ec2h.trim().replace(/^@/, '');
      if (!h && !ec2prof) { setError('Saisis le @ du challenger externe'); return; }
      payload.streamer2_id = null;
      payload.streamer2_name = ec2prof?.display_name || h;
      payload.streamer2_avatar_url = ec2prof?.avatar_url ?? null;
      payload.streamer2_username = h || null;
    }
    setEcBusy(true);
    try {
      const r = await updateMatch(selected.id, payload);
      setSelected(r.data);
      setMatches(prev => prev.map(m => m.id === r.data.id ? r.data : m));
      setEditComp(false);
      setError(null);
    } catch { setError('Erreur lors de la mise à jour des concurrents'); }
    finally { setEcBusy(false); }
  };

  const filtered = matches
    .filter(m => {
      if (filter === 'scheduled' && m.status !== 'scheduled') return false;
      if (filter === 'completed' && m.status !== 'completed') return false;
      const q = search.toLowerCase();
      if (!q) return true;
      return (m.streamer1_name || '').toLowerCase().includes(q) || m.streamer2_name.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      if (filter === 'completed') return b.scheduled_at.localeCompare(a.scheduled_at);
      return a.scheduled_at.localeCompare(b.scheduled_at);
    });

  const resetForm = () => {
    setForm({ match_type: 'officiel', match_label: '', scheduled_date: '', scheduled_time: '21:00', streamer1_id: '', challenger_mode: 'interne', streamer2_id: '' });
    setExternalHandle('');
    setExternalProfile(null);
    setError(null);
  };

  const handleCreate = async () => {
    if (!form.streamer1_id || !form.scheduled_date) { setError('Veuillez remplir tous les champs obligatoires'); return; }
    const scheduled_at = `${form.scheduled_date} ${form.scheduled_time}`;
    let streamer2_id: number | null = null;
    let streamer2_name = '';
    let streamer2_avatar_url: string | null = null;
    let streamer2_username: string | null = null;

    if (form.challenger_mode === 'interne') {
      if (!form.streamer2_id) { setError('Sélectionnez le Challenger 2'); return; }
      streamer2_id = parseInt(form.streamer2_id);
      const s = streamers.find(x => x.id === streamer2_id);
      streamer2_name = s?.display_name || s?.username || '';
      streamer2_username = s?.username || null;
    } else {
      // Externe / hors agence : profil TikTok récupéré OU saisie manuelle du @handle/nom
      const h = externalHandle.trim().replace(/^@/, '');
      if (externalProfile) {
        streamer2_name = externalProfile.display_name;
        streamer2_avatar_url = externalProfile.avatar_url;
        streamer2_username = h || null;
      } else if (externalHandle.trim()) {
        streamer2_name = h;
        streamer2_username = h || null;
      } else {
        setError('Saisissez le @handle ou le nom du challenger externe'); return;
      }
    }

    setSaving(true); setError(null);
    try {
      const r = await createMatch({
        match_type: form.match_type,
        match_label: form.match_type === 'autre' ? form.match_label || null : null,
        scheduled_at,
        streamer1_id: parseInt(form.streamer1_id),
        streamer2_id,
        streamer2_name,
        streamer2_avatar_url,
        streamer2_username,
      });
      await load();
      setShowCreate(false);
      resetForm();
      // Select the newly created match
      setTimeout(() => setSelected(r.data), 100);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const handleFetchTikTok = async () => {
    if (!externalHandle.trim()) return;
    setFetchingProfile(true); setError(null);
    try {
      const r = await fetchTikTokProfile(externalHandle.trim().replace('@', ''));
      setExternalProfile(r.data);
    } catch {
      setError('Impossible de récupérer le profil TikTok');
    } finally {
      setFetchingProfile(false);
    }
  };

  const handleUploadPoster = async (file: File) => {
    if (!selected) return;
    try {
      const r = await uploadMatchPoster(selected.id, file);
      const updated = { ...selected, poster_filename: r.data.poster_filename };
      setSelected(updated);
      setMatches(prev => prev.map(m => m.id === selected.id ? updated : m));
    } catch {
      setError("Erreur upload affiche");
    }
  };

  const handleUpdateScore = async () => {
    if (!selected) return;
    setSaving(true); setError(null);
    try {
      const ff = editScore.forfeit || 'none';
      await updateMatch(selected.id, {
        score1: editScore.score1 !== '' ? parseInt(editScore.score1) : null,
        score2: editScore.score2 !== '' ? parseInt(editScore.score2) : null,
        status: 'completed',
        forfeit: ff,
      });
      const updated: LiveMatch = {
        ...selected,
        score1: editScore.score1 !== '' ? parseInt(editScore.score1) : null,
        score2: editScore.score2 !== '' ? parseInt(editScore.score2) : null,
        status: 'completed',
        forfeit: ff,
      };
      setSelected(updated);
      setMatches(prev => prev.map(m => m.id === selected.id ? updated : m));
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Erreur mise à jour score');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!selected) return;
    const newStatus: 'scheduled' | 'completed' = selected.status === 'scheduled' ? 'completed' : 'scheduled';
    try {
      await updateMatch(selected.id, { status: newStatus });
      const updated = { ...selected, status: newStatus };
      setSelected(updated);
      setMatches(prev => prev.map(m => m.id === selected.id ? updated : m));
    } catch {}
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await deleteMatch(selected.id);
      setMatches(prev => prev.filter(m => m.id !== selected.id));
      setSelected(null);
      setDeleteConfirm(false);
    } catch {
      setError('Erreur suppression');
    }
  };

  const handleAcceptRequest = async (req: MatchRequest) => {
    setRequestAction(prev => ({ ...prev, [req.id]: 'accepting' }));
    try {
      const match = await acceptMatchRequest(req.id);
      setMatches(prev => [match.data, ...prev]);
      setRequests(prev => prev.filter(r => r.id !== req.id));
      setSelected(match.data);
      setShowCreate(false);
    } catch { setError('Erreur lors de l\'acceptation'); }
    setRequestAction(prev => { const n = { ...prev }; delete n[req.id]; return n; });
  };

  const handleRejectRequest = async (req: MatchRequest, notes?: string) => {
    setRequestAction(prev => ({ ...prev, [req.id]: 'rejecting' }));
    try {
      await updateMatchRequest(req.id, { status: 'rejected', admin_notes: notes || null });
      setRequests(prev => prev.filter(r => r.id !== req.id));
    } catch { setError('Erreur lors du refus'); }
    setRequestAction(prev => { const n = { ...prev }; delete n[req.id]; return n; });
  };

  const handleDeleteRequest = async (id: number) => {
    try {
      await deleteMatchRequest(id);
      setRequests(prev => prev.filter(r => r.id !== id));
    } catch { setError('Erreur suppression demande'); }
  };

  // ── Sub-panels ──────────────────────────────────────────────

  const listPanel = (
    <div style={{ width: isMobile ? '100%' : 340, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: isMobile ? 'none' : '1px solid #e2e8f0', height: '100%', background: '#f8fafc' }}>
      <div style={{ padding: '12px 14px', borderBottom: '1px solid #e2e8f0', background: '#fff', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un match…" style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#f8fafc', boxSizing: 'border-box', outline: 'none' }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {(['all', 'scheduled', 'completed'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} style={{ flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', fontSize: 11, fontWeight: filter === f ? 700 : 400, cursor: 'pointer', background: filter === f ? '#2563eb' : '#f1f5f9', color: filter === f ? '#fff' : '#64748b' }}>
              {f === 'all' ? 'Tous' : f === 'scheduled' ? '🗓 À venir' : '✅ Terminés'}
            </button>
          ))}
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 12 }}>
        {/* Demandes en attente */}
        {requests.length > 0 && (
          <div style={{ marginBottom: 10 }}>
            <button onClick={() => setShowRequests(v => !v)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, border: '1px solid #fde68a', background: '#fffbeb', cursor: 'pointer', marginBottom: showRequests ? 8 : 0 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#92400e' }}>
                📬 Demandes en attente
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ background: '#f59e0b', color: '#fff', borderRadius: 20, fontSize: 10, fontWeight: 700, padding: '1px 7px' }}>{requests.length}</span>
                <span style={{ color: '#92400e', fontSize: 12 }}>{showRequests ? '▲' : '▼'}</span>
              </span>
            </button>
            {showRequests && requests.map(req => (
              <RequestAdminCard key={req.id}
                req={req}
                streamers={streamers}
                actionState={requestAction[req.id]}
                onAccept={() => handleAcceptRequest(req)}
                onReject={(notes) => handleRejectRequest(req, notes)}
                onDelete={() => handleDeleteRequest(req.id)}
              />
            ))}
            <div style={{ height: 1, background: '#e2e8f0', margin: '10px 0' }} />
          </div>
        )}

        {loading && <div style={{ textAlign: 'center', color: '#94a3b8', padding: 24, fontSize: 13 }}>Chargement…</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', padding: 32, fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🥊</div>
            Aucun match trouvé
          </div>
        )}
        {filtered.map(m => (
          <MatchCard key={m.id} match={m} selected={selected?.id === m.id && !showCreate} onClick={() => { setSelected(m); setShowCreate(false); }} />
        ))}
      </div>
      <div style={{ padding: 12, borderTop: '1px solid #e2e8f0', background: '#fff' }}>
        <button onClick={() => { setShowCreate(true); setSelected(null); resetForm(); }} style={{ width: '100%', padding: '10px 0', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + Créer un match
        </button>
      </div>
    </div>
  );

  const createPanel = (
    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 16 : 32, background: '#f8fafc' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Nouveau match</h2>
          <button onClick={() => { setShowCreate(false); setError(null); }} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 22, lineHeight: 1, padding: 4 }}>×</button>
        </div>

        {/* Type */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.6 }}>Type de match</label>
          <div style={{ display: 'flex', gap: 8 }}>
            {[{ v: 'officiel', l: '🏆 Officiel' }, { v: 'anniversaire', l: '🎂 Anniversaire' }].map(({ v, l }) => (
              <button key={v} onClick={() => setForm(f => ({ ...f, match_type: v }))} style={{ flex: 1, padding: '8px 4px', borderRadius: 8, border: `2px solid ${form.match_type === v ? (TYPE_COLOR[v] || '#2563eb') : '#e2e8f0'}`, background: form.match_type === v ? (TYPE_COLOR[v] || '#2563eb') + '12' : '#fff', color: form.match_type === v ? (TYPE_COLOR[v] || '#2563eb') : '#64748b', fontSize: 12, fontWeight: form.match_type === v ? 700 : 400, cursor: 'pointer' }}>
                {l}
              </button>
            ))}
          </div>
        </div>

        {/* Date + heure */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.6 }}>Date &amp; heure</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input type="date" value={form.scheduled_date} onChange={e => setForm(f => ({ ...f, scheduled_date: e.target.value }))} style={{ flex: 2, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} />
            <input type="time" value={form.scheduled_time} onChange={e => setForm(f => ({ ...f, scheduled_time: e.target.value }))} style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} />
          </div>
        </div>

        {/* Streamer 1 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.6 }}>Challenger 1 (agence)</label>
          <select value={form.streamer1_id} onChange={e => setForm(f => ({ ...f, streamer1_id: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', outline: 'none' }}>
            <option value="">Sélectionner un streamer…</option>
            {streamers.map(s => <option key={s.id} value={s.id}>{s.display_name || s.username}</option>)}
          </select>
          {/* Rappel SMS de ce streamer avant ses matchs */}
          {form.streamer1_id && !matchReminderLoading && matchReminder && (
            <div style={{ marginTop: 10, padding: 10, borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => saveMatchReminder({ enabled: !matchReminder.enabled })} disabled={savingMatchReminder}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}>
                  <span style={{ width: 38, height: 22, borderRadius: 999, background: matchReminder.enabled ? '#16a34a' : '#cbd5e1', position: 'relative', flexShrink: 0, display: 'inline-block' }}>
                    <span style={{ position: 'absolute', top: 2, left: matchReminder.enabled ? 18 : 2, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .15s' }} />
                  </span>
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: '#0f172a' }}>🔔 Rappel SMS avant le match</span>
                </button>
                {matchReminder.enabled && (
                  <select value={matchReminder.lead_min} onChange={e => saveMatchReminder({ lead_min: Number(e.target.value) })} disabled={savingMatchReminder}
                    style={{ marginLeft: 'auto', padding: '5px 8px', borderRadius: 7, border: '1px solid #e2e8f0', fontSize: 12, background: '#fff', cursor: 'pointer', color: '#0f172a' }}>
                    {[15, 30, 60, 120, 180].map(v => <option key={v} value={v}>{v < 60 ? `${v} min avant` : `${v / 60} h avant`}</option>)}
                  </select>
                )}
              </div>
              {!matchReminder.phone && (
                <div style={{ marginTop: 8, fontSize: 11, color: '#b45309', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '5px 8px' }}>
                  ⚠️ Ce streamer n'a pas de numéro de mobile dans son profil — il ne recevra pas de SMS.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Streamer 2 */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.6 }}>Challenger 2</label>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {(['interne', 'externe'] as const).map(m => (
              <button key={m} onClick={() => { setForm(f => ({ ...f, challenger_mode: m })); setExternalProfile(null); }} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `2px solid ${form.challenger_mode === m ? '#2563eb' : '#e2e8f0'}`, background: form.challenger_mode === m ? '#eff6ff' : '#fff', color: form.challenger_mode === m ? '#2563eb' : '#64748b', fontSize: 12, fontWeight: form.challenger_mode === m ? 700 : 400, cursor: 'pointer' }}>
                {m === 'interne' ? '🏠 Agence' : '🌐 Externe'}
              </button>
            ))}
          </div>
          {form.challenger_mode === 'interne' ? (
            <select value={form.streamer2_id} onChange={e => setForm(f => ({ ...f, streamer2_id: e.target.value }))} style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, background: '#fff', outline: 'none' }}>
              <option value="">Sélectionner un streamer…</option>
              {streamers.filter(s => String(s.id) !== form.streamer1_id).map(s => <option key={s.id} value={s.id}>{s.display_name || s.username}</option>)}
            </select>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                <input value={externalHandle} onChange={e => setExternalHandle(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleFetchTikTok()} placeholder="@handle TikTok" style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} />
                <button onClick={handleFetchTikTok} disabled={fetchingProfile || !externalHandle.trim()} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontSize: 12, fontWeight: 600, cursor: fetchingProfile || !externalHandle.trim() ? 'not-allowed' : 'pointer', opacity: fetchingProfile || !externalHandle.trim() ? 0.5 : 1 }}>
                  {fetchingProfile ? '…' : 'Récupérer'}
                </button>
              </div>
              {externalProfile ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
                  <MiniAvatar url={externalProfile.avatar_url} name={externalProfile.display_name} size={36} />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{externalProfile.display_name}</div>
                    <div style={{ fontSize: 11, color: '#16a34a' }}>✓ Profil récupéré</div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: '#64748b' }}>
                  « Récupérer » est optionnel (récupère l'avatar). Tu peux créer le match directement : le @handle/nom saisi sera utilisé comme Challenger 2.
                </div>
              )}
            </div>
          )}
        </div>

        {error && <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13, marginBottom: 16 }}>{error}</div>}

        <button onClick={handleCreate} disabled={saving} style={{ width: '100%', padding: '12px 0', borderRadius: 10, border: 'none', background: '#2563eb', color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Création…' : '🥊 Créer le match'}
        </button>
      </div>
    </div>
  );

  const detailPanel = selected ? (
    <div style={{ flex: 1, overflowY: 'auto', padding: isMobile ? 16 : 32, background: '#f8fafc' }}>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>

        {/* Header card */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 20, background: (TYPE_COLOR[selected.match_type] || '#64748b') + '18', color: TYPE_COLOR[selected.match_type] || '#64748b' }}>
              {TYPE_LABEL[selected.match_type] || selected.match_label || selected.match_type}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button onClick={() => setEditComp(o => !o)} title="Modifier les concurrents"
                style={{ width: 28, height: 28, borderRadius: '50%', border: `1px solid ${editComp ? '#2563eb' : '#e2e8f0'}`, background: editComp ? '#eff6ff' : '#f8fafc', color: '#2563eb', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                ✏️
              </button>
              <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, background: selected.status === 'completed' ? '#dcfce7' : '#fef3c7', color: selected.status === 'completed' ? '#16a34a' : '#92400e', fontWeight: 700 }}>
                {selected.status === 'completed' ? '✅ Terminé' : '🗓 Programmé'}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: isMobile ? 10 : 24 }}>
            {/* Challenger 1 */}
            <div style={{ textAlign: 'center', width: isMobile ? 90 : 120, flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <MiniAvatar url={selected.streamer1_avatar} name={selected.streamer1_name} size={isMobile ? 52 : 68} />
              </div>
              <div style={{ fontSize: isMobile ? 11 : 13, fontWeight: 700, color: '#0f172a', marginTop: 8, lineHeight: '1.25', wordBreak: 'break-word', hyphens: 'auto' } as React.CSSProperties}>
                {selected.streamer1_name || '?'}
              </div>
            </div>
            {/* VS / score */}
            <div style={{ textAlign: 'center', flex: 1, minWidth: 0 }}>
              {selected.status === 'completed' && selected.score1 != null && selected.score2 != null ? (
                <div style={{ fontSize: isMobile ? 26 : 34, fontWeight: 900, color: '#0f172a', letterSpacing: 2 }}>{selected.score1} — {selected.score2}</div>
              ) : (
                <div style={{ fontSize: isMobile ? 26 : 34, fontWeight: 900, color: '#94a3b8' }}>VS</div>
              )}
              {selected.forfeit && selected.forfeit !== 'none' && (
                <div style={{ fontSize: 12, color: '#dc2626', fontWeight: 700, marginTop: 6 }}>
                  ⚠ Forfait — {selected.forfeit === 'streamer1' ? selected.streamer2_name : (selected.streamer1_name || 'Challenger 1')} gagne
                </div>
              )}
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 6, lineHeight: 1.4 }}>{fmtDate(selected.scheduled_at)}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{fmtTime(selected.scheduled_at)}</div>
            </div>
            {/* Challenger 2 */}
            <div style={{ textAlign: 'center', width: isMobile ? 90 : 120, flexShrink: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <MiniAvatar url={selected.streamer2_avatar_url} name={selected.streamer2_name} size={isMobile ? 52 : 68} />
              </div>
              <div style={{ fontSize: isMobile ? 11 : 13, fontWeight: 700, color: '#0f172a', marginTop: 8, lineHeight: '1.25', wordBreak: 'break-word', hyphens: 'auto' } as React.CSSProperties}>
                {selected.streamer2_name || '?'}
              </div>
            </div>
          </div>
          {editComp && (
            <div style={{ marginTop: 18, borderTop: '1px solid #f1f5f9', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.6 }}>Challenger 1 (agence)</label>
                <select value={ec1} onChange={e => setEc1(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }}>
                  <option value="">— Choisir —</option>
                  {streamers.map(s => <option key={s.id} value={s.id}>{s.display_name || s.username}</option>)}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#94a3b8', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.6 }}>Challenger 2</label>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {(['interne', 'externe'] as const).map(m => (
                    <button key={m} onClick={() => { setEc2mode(m); setEc2prof(null); }} style={{ flex: 1, padding: '7px 0', borderRadius: 8, border: `2px solid ${ec2mode === m ? '#2563eb' : '#e2e8f0'}`, background: ec2mode === m ? '#eff6ff' : '#fff', color: ec2mode === m ? '#2563eb' : '#64748b', fontSize: 12, fontWeight: ec2mode === m ? 700 : 400, cursor: 'pointer' }}>
                      {m === 'interne' ? '🏠 Agence' : '🌐 Externe'}
                    </button>
                  ))}
                </div>
                {ec2mode === 'interne' ? (
                  <select value={ec2id} onChange={e => setEc2id(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }}>
                    <option value="">— Choisir —</option>
                    {streamers.map(s => <option key={s.id} value={s.id}>{s.display_name || s.username}</option>)}
                  </select>
                ) : (
                  <>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={ec2h} onChange={e => setEc2h(e.target.value)} onKeyDown={e => e.key === 'Enter' && fetchEc2()} placeholder="@handle TikTok" style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none' }} />
                      <button onClick={fetchEc2} disabled={!ec2h.trim()} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontSize: 12, fontWeight: 600, cursor: !ec2h.trim() ? 'not-allowed' : 'pointer', opacity: !ec2h.trim() ? 0.5 : 1 }}>Récupérer</button>
                    </div>
                    {ec2prof && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                        <MiniAvatar url={ec2prof.avatar_url} name={ec2prof.display_name} size={36} />
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{ec2prof.display_name}</div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <button onClick={saveCompetitors} disabled={ecBusy} style={{ alignSelf: 'flex-start', padding: '9px 18px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: ecBusy ? 'wait' : 'pointer' }}>
                {ecBusy ? '…' : '💾 Enregistrer les concurrents'}
              </button>
            </div>
          )}
        </div>

        {/* Poster section */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>🖼 Affiche</h3>
          {selected.poster_filename && (
            <div style={{ marginBottom: 16, textAlign: 'center', background: '#f8fafc', borderRadius: 10, padding: 12 }}>
              <img src={getMatchPosterUrl(selected.poster_filename) + '?t=' + Date.now()} alt="Affiche" style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 8, objectFit: 'contain' }} />
            </div>
          )}
          <input ref={posterInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) handleUploadPoster(f); e.target.value = ''; }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
            <button onClick={() => setShowEditor(true)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#7c3aed', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              🎨 Éditeur d'affiche
            </button>
            <button onClick={() => posterInputRef.current?.click()} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              📥 Importer
            </button>
            <a href="https://www.canva.com" target="_blank" rel="noreferrer" style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              🎨 Créer sur Canva
            </a>
            {selected.poster_filename && (
              <button onClick={async () => {
                try {
                  const resp = await fetch(getMatchPosterUrl(selected.poster_filename!), { credentials: 'include' });
                  const blob = await resp.blob();
                  const objUrl = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = objUrl; a.download = `affiche_match_${selected.id}.png`;
                  document.body.appendChild(a); a.click(); a.remove();
                  setTimeout(() => URL.revokeObjectURL(objUrl), 2000);
                } catch { window.open(getMatchPosterUrl(selected.poster_filename!), '_blank'); }
              }} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#059669', color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center' }}>
                📥 Télécharger
              </button>
            )}
          </div>
        </div>

        {/* Score section */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e2e8f0', padding: 24, marginBottom: 16 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>🎯 Score</h3>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.streamer1_name || 'Challenger 1'}</label>
              <input type="number" min={0} value={editScore.score1} onChange={e => setEditScore(s => ({ ...s, score1: e.target.value }))} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 22, fontWeight: 800, textAlign: 'center', boxSizing: 'border-box', outline: 'none' }} />
            </div>
            <div style={{ fontSize: 22, fontWeight: 900, color: '#cbd5e1', paddingBottom: 10, flexShrink: 0 }}>—</div>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginBottom: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selected.streamer2_name}</label>
              <input type="number" min={0} value={editScore.score2} onChange={e => setEditScore(s => ({ ...s, score2: e.target.value }))} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: 22, fontWeight: 800, textAlign: 'center', boxSizing: 'border-box', outline: 'none' }} />
            </div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>Forfait (live coupé, absent…)</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {([
                { v: '', lbl: 'Aucun forfait' },
                { v: 'streamer1', lbl: `Forfait : ${selected.streamer1_name || 'Challenger 1'}` },
                { v: 'streamer2', lbl: `Forfait : ${selected.streamer2_name}` },
              ] as const).map(opt => {
                const active = editScore.forfeit === opt.v;
                return (
                  <button key={opt.v || 'none'} type="button" onClick={() => setEditScore(s => ({ ...s, forfeit: opt.v }))}
                    style={{ padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: active ? '1px solid #dc2626' : '1px solid #e2e8f0',
                      background: active ? '#fef2f2' : '#f8fafc',
                      color: active ? '#dc2626' : '#374151' }}>
                    {active && opt.v ? '⚠ ' : ''}{opt.lbl}
                  </button>
                );
              })}
            </div>
            {editScore.forfeit && (
              <div style={{ fontSize: 11, color: '#dc2626', marginTop: 6 }}>
                Vainqueur déclaré : {editScore.forfeit === 'streamer1' ? selected.streamer2_name : (selected.streamer1_name || 'Challenger 1')}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={handleUpdateScore} disabled={saving} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#0f172a', color: '#fff', fontSize: 13, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.6 : 1, minWidth: 140 }}>
              {saving ? 'Enregistrement…' : '✅ Enregistrer le score'}
            </button>
            <button onClick={handleToggleStatus} style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#f8fafc', color: '#374151', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {selected.status === 'scheduled' ? '▶ Terminer' : '↩ Remettre en attente'}
            </button>
          </div>
        </div>

        {/* Delete section */}
        <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #fee2e2', padding: 20 }}>
          {!deleteConfirm ? (
            <button onClick={() => setDeleteConfirm(true)} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              🗑 Supprimer ce match
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13, color: '#dc2626', fontWeight: 600 }}>Supprimer définitivement ?</span>
              <button onClick={handleDelete} style={{ padding: '7px 14px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Oui, supprimer</button>
              <button onClick={() => setDeleteConfirm(false)} style={{ padding: '7px 14px', borderRadius: 8, border: '1px solid #e2e8f0', background: '#fff', color: '#374151', fontSize: 12, cursor: 'pointer' }}>Annuler</button>
            </div>
          )}
        </div>

        {error && <div style={{ marginTop: 12, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>{error}</div>}
      </div>
    </div>
  ) : null;

  const emptyState = (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>🥊</div>
        <div style={{ fontSize: 14, fontWeight: 500 }}>Sélectionnez un match</div>
        <div style={{ fontSize: 13, marginTop: 6 }}>ou créez-en un nouveau</div>
      </div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: '#f8fafc' }}>
      <div style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0, padding: isMobile ? '14px 14px 12px' : '22px 32px 18px' }}>
        <h1 style={{ margin: 0, color: '#0f172a', fontSize: isMobile ? 18 : 22, fontWeight: 700 }}>Lives Matchs</h1>
        <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>Gérez les matchs des streamers et générez leurs affiches</p>
      </div>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {isMobile ? (
          selected || showCreate ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <div style={{ padding: '10px 16px', background: '#fff', borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
                <button onClick={() => { setSelected(null); setShowCreate(false); setError(null); }} style={{ border: 'none', background: 'none', color: '#2563eb', fontSize: 13, cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                  ← Retour
                </button>
              </div>
              <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {showCreate ? createPanel : detailPanel}
              </div>
            </div>
          ) : (
            <div style={{ flex: 1, overflow: 'hidden' }}>{listPanel}</div>
          )
        ) : (
          <>
            {listPanel}
            {showCreate ? createPanel : (selected ? detailPanel : emptyState)}
          </>
        )}
      </div>

      {showEditor && selected && (
        <PosterEditor
          match={selected}
          isAdmin={isAdmin}
          isMobile={isMobile}
          onClose={() => setShowEditor(false)}
          onSaved={(filename) => {
            const updated = { ...selected, poster_filename: filename };
            setSelected(updated);
            setMatches(prev => prev.map(m => m.id === selected.id ? updated : m));
          }}
          onBackgroundUpdated={(filename) => {
            const updated = { ...selected, background_filename: filename };
            setSelected(updated);
            setMatches(prev => prev.map(m => m.id === selected.id ? updated : m));
          }}
        />
      )}

    </div>
  );
}
